/**
 * SIL-2333: calls for the notes and attachments of a bench run. Customer-supplied, not verified.
 * Upload is presign (server builds the key) -> POST the file straight to S3 -> finalize (server HeadObject).
 */
import type {
  BenchAttachmentPresignResponse,
  BenchRunAnnotations,
} from '@siliconx/shared/bench';
import { api, throwIfNotOk } from './api';

export async function getBenchRunAnnotations(runId: string): Promise<BenchRunAnnotations> {
  const res = await api.api.client.bench.runs[':id'].annotations.$get({ param: { id: runId } });
  await throwIfNotOk(res as unknown as Response);
  return (await res.json()) as unknown as BenchRunAnnotations;
}

export async function saveBenchRunNotes(runId: string, notes: string): Promise<void> {
  const res = await api.api.client.bench.runs[':id'].notes.$patch({ param: { id: runId }, json: { notes } });
  await throwIfNotOk(res as unknown as Response);
}

export async function uploadBenchRunAttachment(runId: string, file: File): Promise<void> {
  const presign = await api.api.client.bench.runs[':id'].attachments.$post({
    param: { id: runId },
    json: { fileName: file.name, contentType: file.type, contentLength: file.size },
  });
  await throwIfNotOk(presign as unknown as Response);
  const { id, upload } = (await presign.json()) as unknown as BenchAttachmentPresignResponse;

  // S3 presigned POST: the policy fields come first and the file part last.
  const form = new FormData();
  for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
  form.append('file', file);
  const put = await fetch(upload.url, { method: 'POST', body: form });
  if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`);

  const fin = await api.api.client.bench.runs[':id'].attachments[':attachmentId'].finalize.$post({
    param: { id: runId, attachmentId: id },
  });
  await throwIfNotOk(fin as unknown as Response);
}

export async function getBenchAttachmentDownloadUrl(runId: string, attachmentId: string): Promise<string> {
  const res = await api.api.client.bench.runs[':id'].attachments[':attachmentId'].download.$get({
    param: { id: runId, attachmentId },
  });
  await throwIfNotOk(res as unknown as Response);
  return ((await res.json()) as unknown as { downloadUrl: string }).downloadUrl;
}

export async function deleteBenchRunAttachment(runId: string, attachmentId: string): Promise<void> {
  const res = await api.api.client.bench.runs[':id'].attachments[':attachmentId'].$delete({
    param: { id: runId, attachmentId },
  });
  await throwIfNotOk(res as unknown as Response);
}
